import type { FanResponseDto, OnlyFansList } from '@api'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form/form-field'
import { useManageUserLists } from '@/modules/chatting/hooks/use-manage-user-lists'

import { DialogProps } from '../types'
import {
  CREATE_LIST_FORM_ID,
  CreateListFormValues,
  createListFormResolver,
} from './validation'

interface Props extends DialogProps {
  forUserId?: FanResponseDto['id']
  onSuccess?: (list: OnlyFansList) => void
}

export const CreateListDialog = ({
  isOpen,
  onClose,
  forUserId,
  onSuccess,
}: Props) => {
  const form = useForm<CreateListFormValues>({
    defaultValues: { name: '' },
    resolver: createListFormResolver,
  })
  const { createUserListMutation } = useManageUserLists()

  const onSubmit = (values: CreateListFormValues) =>
    createUserListMutation
      .mutateAsync({ name: values.name, forUserId })
      .then((list) => {
        onSuccess?.(list)
        onClose()
      })

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      bigDialog
      panelClassName="lg:max-w-[533px]"
      afterLeave={form.reset}
    >
      <form
        id={CREATE_LIST_FORM_ID}
        className="space-y-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Dialog.Header>
          <Dialog.Title>Create New List</Dialog.Title>
        </Dialog.Header>
        <FormProvider {...form}>
          <FormField
            label="List Name"
            name="name"
          />
        </FormProvider>
        <Dialog.ClosableFooter
          onClose={onClose}
          disabled={form.formState.isSubmitting}
        >
          <Button
            form={CREATE_LIST_FORM_ID}
            isLoading={form.formState.isSubmitting}
            disabled={!form.formState.isDirty}
          >
            Save
          </Button>
        </Dialog.ClosableFooter>
      </form>
    </Dialog>
  )
}
